import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import axiosInstance from '@/lib/axios';

interface ReportDialogProps {
  postId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReported?: () => void;
  entityLabel?: 'post' | 'poll';
}

const REPORT_REASONS = [
  'Sexual content',
  'Violent or repulsive content',
  'Hateful or abusive content',
  'Harassment or bullying',
  'Harmful or dangerous acts',
  'Suicide, self-harm or eating disorders',
  'Misinformation',
  'Child abuse',
  'Promotes terrorism',
  'Spam or misleading',
  'Legal issue',
  'Other',
] as const;

export function ReportDialog({
  postId,
  open,
  onOpenChange,
  onReported,
  entityLabel = 'post',
}: ReportDialogProps) {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [otherText, setOtherText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReason) return;

    setIsSubmitting(true);
    try {
      await axiosInstance.post(`/api/posts/${postId}/report`, {
        reason: selectedReason,
        details: selectedReason === 'Other' ? otherText : null,
      });

      toast.success(
        entityLabel === 'poll' ? 'Poll reported successfully' : 'Report submitted successfully'
      );
      onOpenChange(false);
      setSelectedReason('');
      setOtherText('');
      onReported?.();
    } catch (error: unknown) {
      console.error(error);
      let errorMessage = entityLabel === 'poll' ? 'Failed to report poll' : 'Failed to submit report';
      if (error && typeof error === 'object' && 'response' in error) {
        const errObj = error as { response?: { data?: { message?: string; error?: string } } };
        if (errObj.response?.data?.message) {
          errorMessage = errObj.response.data.message;
        } else if (errObj.response?.data?.error) {
          errorMessage = errObj.response.data.error;
        }
      }
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] h-[80vh] flex flex-col p-0 gap-0 border-none shadow-2xl">
        <DialogHeader className="px-6 py-5 shrink-0 flex flex-col gap-3">
          <DialogTitle className="text-xl font-semibold">
            {entityLabel === 'poll' ? 'Report poll' : 'Report'}
          </DialogTitle>
          <div className="flex flex-col gap-1">
            <h3 className="font-bold text-lg text-foreground">What's going on?</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              We'll check for all community guidelines, so don't worry about making the perfect
              choice.
            </p>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 px-6">
          <form id="report-form" onSubmit={handleSubmit} className="space-y-4 pb-4 pr-2">
            <RadioGroup value={selectedReason} onValueChange={setSelectedReason} className="gap-5">
              {REPORT_REASONS.map((reason) => (
                <div key={reason} className="flex items-center space-x-3">
                  <RadioGroupItem value={reason} id={reason} className="h-5 w-5" />
                  <Label htmlFor={reason} className="font-normal text-base cursor-pointer">
                    {reason}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {selectedReason === 'Other' && (
              <div className="mt-4 animate-in fade-in slide-in-from-top-2 ml-2">
                <Label htmlFor="other-text" className="sr-only">
                  Other reason
                </Label>
                <Textarea
                  id="other-text"
                  placeholder="Please provide more details about the issue..."
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  className="resize-none mt-2"
                  rows={4}
                  required
                />
              </div>
            )}
          </form>
        </ScrollArea>

        <div className="p-4 mt-auto bg-background shrink-0 flex justify-end">
          <Button
            type="submit"
            form="report-form"
            disabled={
              !selectedReason || (selectedReason === 'Other' && !otherText.trim()) || isSubmitting
            }
            className="w-full rounded-full font-medium"
          >
            Next
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
